import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, CircularProgress, Collapse, Typography } from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { getRouteTrips, getRouteShape, type RouteTrips, type NetworkStop } from '../../services/network/networkApi';
import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';
import { buildTimetableMatrix } from '../../utils/fixedRoutes/buildTimetableMatrix';
import { routeTripsToJourneys } from '../../utils/fixedRoutes/timetableGridAdapters';
import TimetableGridView from './TimetableGridView';
import { generateRouteColor } from '../../utils/fixedRoutes/routeToLineConverter';

/**
 * Trips by day & direction for a selected hosted-network route (PRD-05), rendered as a
 * bustimes.org-style timetable grid (stops = rows, journeys = columns) with a companion
 * route map. Lazy: only fetches when expanded. Served by the backend
 * `trips_by_day_direction` (real GTFS stop_times, integer seconds since midnight — after-
 * midnight calls exceed 86400 and are shown as `+1`, never normalized). The route shape
 * supplies stop names/coordinates and the map line for the chosen direction.
 */

interface Direction {
  directionId: number;
  headsign?: string | null;
  tripCount?: number;
}

interface Props {
  feedId: string;
  routeId: string;
  version: string | null;
  directions: Direction[];
  /** Day-of-week names the route runs (lowercase, e.g. 'monday'). Empty = unknown → all enabled. */
  serviceDays: string[];
  defaultDirection: number | null;
}

const DAYS: Array<{ value: string; key: string }> = [
  { value: 'monday', key: 'mon' },
  { value: 'tuesday', key: 'tue' },
  { value: 'wednesday', key: 'wed' },
  { value: 'thursday', key: 'thu' },
  { value: 'friday', key: 'fri' },
  { value: 'saturday', key: 'sat' },
  { value: 'sunday', key: 'sun' },
];

const TRIP_CAP = 120; // cap columns rendered; beyond this the grid stays responsive

function TripsSection({ feedId, routeId, version, directions, serviceDays, defaultDirection }: Props): React.ReactElement {
  const { t } = useTranslation(['mapCore']);
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState('monday');
  const [direction, setDirection] = useState<number>(defaultDirection ?? directions[0]?.directionId ?? 0);
  const [state, setState] = useState<{ loading: boolean; error: boolean; data: RouteTrips | null }>({
    loading: false,
    error: false,
    data: null,
  });
  // Route shape for the chosen direction → stop names/coords + the map line.
  const [shape, setShape] = useState<{ coordinates: Array<[number, number]>; stops: NetworkStop[] }>({
    coordinates: [],
    stops: [],
  });

  const dirOptions: Direction[] = directions.length ? directions : [{ directionId: 0 }];
  const dayEnabled = (value: string) => serviceDays.length === 0 || serviceDays.includes(value);
  const dirEnabled = (d: Direction) => (d.tripCount ?? 1) > 0;

  // Snap the selected day to a real service day once the route's service days load.
  useEffect(() => {
    if (serviceDays.length && !serviceDays.includes(day)) {
      const first = DAYS.find((d) => serviceDays.includes(d.value));
      if (first) setDay(first.value);
    }
  }, [serviceDays, day]);

  // Snap the direction to one that has trips once directions load.
  useEffect(() => {
    const enabled = dirOptions.filter(dirEnabled);
    if (enabled.length && !enabled.some((d) => d.directionId === direction)) {
      setDirection(enabled[0].directionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directions, direction]);

  // Lazy fetch trips — only while open, and on day/direction/route change.
  useEffect(() => {
    if (!open || !feedId || !routeId) return undefined;
    const ctl = new AbortController();
    setState((prev) => ({ ...prev, loading: true, error: false }));
    getRouteTrips(feedId, routeId, { day, direction, version: version ?? undefined, signal: ctl.signal })
      .then((data) => {
        setState({ loading: false, error: false, data });
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.network_trips_viewed, {
          feed_id: feedId,
          route_id: routeId,
          day,
          direction,
          trip_count: data.tripCount,
          timestamp: new Date().toISOString(),
        });
      })
      .catch(() => {
        if (!ctl.signal.aborted) setState({ loading: false, error: true, data: null });
      });
    return () => ctl.abort();
  }, [open, feedId, routeId, day, direction, version]);

  // Lazy fetch the route shape for the chosen direction (stop names/coords + map line).
  useEffect(() => {
    if (!open || !feedId || !routeId) return undefined;
    const ctl = new AbortController();
    getRouteShape(feedId, routeId, { direction, version: version ?? undefined, signal: ctl.signal })
      .then((shapes) => {
        const s = shapes[0];
        setShape({ coordinates: s?.coordinates ?? [], stops: s?.stops ?? [] });
      })
      .catch(() => {
        if (!ctl.signal.aborted) setShape({ coordinates: [], stops: [] });
      });
    return () => ctl.abort();
  }, [open, feedId, routeId, direction, version]);

  // Build the bustimes-style matrix from the trips, resolving names/coords from the shape.
  const matrix = useMemo(() => {
    if (!state.data) return null;
    const stopMeta = new Map<string, { name?: string | null; lat?: number; lng?: number }>();
    shape.stops.forEach((s) => stopMeta.set(s.stopId, { name: s.stopName, lat: s.stopLat, lng: s.stopLon }));
    const cappedTrips = state.data.trips.slice(0, TRIP_CAP);
    const journeys = routeTripsToJourneys({ ...state.data, trips: cappedTrips });
    return buildTimetableMatrix(journeys, (id) => stopMeta.get(id));
  }, [state.data, shape.stops]);

  const miniMapStops = useMemo(
    () => shape.stops.map((s) => ({ stopId: s.stopId, name: s.stopName, lat: s.stopLat, lng: s.stopLon })),
    [shape.stops],
  );

  // Corridor heading from the spine's endpoints, e.g. "Carlisle … – West Cumbria Hospital".
  const heading = useMemo(() => {
    if (!matrix || matrix.stops.length < 2) return undefined;
    const first = matrix.stops[0].name;
    const last = matrix.stops[matrix.stops.length - 1].name;
    return `${first} – ${last}`;
  }, [matrix]);

  const tripsTotal = state.data?.trips.length ?? 0;
  const routeColor = generateRouteColor(routeId);

  return (
    <Box sx={{ pl: 5.5, pr: 1, pb: 0.5 }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: 'text.secondary', py: 0.25 }}
      >
        {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        <ScheduleIcon fontSize="small" />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {t('networkRoutes.trips.heading')}
        </Typography>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ pt: 0.5 }}>
          {/* Day-of-week */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
            {DAYS.map((d) => {
              const enabled = dayEnabled(d.value);
              return (
                <Chip
                  key={d.value}
                  size="small"
                  label={t(`networkRoutes.trips.days.${d.key}`)}
                  color={day === d.value ? 'primary' : 'default'}
                  variant={day === d.value ? 'filled' : 'outlined'}
                  disabled={!enabled}
                  onClick={enabled ? () => setDay(d.value) : undefined}
                  sx={{ height: 22 }}
                />
              );
            })}
          </Box>

          {/* Direction */}
          {dirOptions.length > 1 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
              {dirOptions.map((d) => {
                const enabled = dirEnabled(d);
                return (
                  <Chip
                    key={d.directionId}
                    size="small"
                    label={d.headsign || t('networkRoutes.directionLabel', { id: d.directionId })}
                    color={direction === d.directionId ? 'secondary' : 'default'}
                    variant={direction === d.directionId ? 'filled' : 'outlined'}
                    disabled={!enabled}
                    onClick={enabled ? () => setDirection(d.directionId) : undefined}
                    sx={{ height: 22, maxWidth: '100%' }}
                  />
                );
              })}
            </Box>
          )}

          {state.loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', py: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant="caption">{t('networkRoutes.trips.loading')}</Typography>
            </Box>
          )}
          {!state.loading && state.error && (
            <Typography variant="caption" color="error">
              {t('networkRoutes.trips.error')}
            </Typography>
          )}
          {!state.loading && !state.error && state.data && (
            state.data.tripCount === 0 || !matrix ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('networkRoutes.trips.none')}
              </Typography>
            ) : (
              <TimetableGridView
                matrix={matrix}
                routeColor={routeColor}
                heading={heading}
                miniMap={{ coordinates: shape.coordinates, stops: miniMapStops }}
                onModeChange={(mode) =>
                  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.timetable_grid_mode_toggled, {
                    surface: 'network',
                    mode,
                    feed_id: feedId,
                    route_id: routeId,
                    timestamp: new Date().toISOString(),
                  })
                }
                summary={
                  <Typography variant="caption" color="text.secondary">
                    {state.data.headwayMin != null
                      ? t('networkRoutes.trips.summaryHeadway', {
                          count: state.data.tripCount,
                          minutes: state.data.headwayMin,
                        })
                      : t('networkRoutes.trips.summary', { count: state.data.tripCount })}
                    {tripsTotal > TRIP_CAP && ` · ${t('networkRoutes.trips.capped', { shown: TRIP_CAP, total: tripsTotal })}`}
                  </Typography>
                }
              />
            )
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default TripsSection;
